import React, { useState } from 'react';
import {
  Crown,
  Edit,
  Trash2,
  UserPlus,
  X,
  Phone,
  CreditCard,
  MapPin,
  Gift,
  Calendar,
  ShieldOff,
  Eye,
  Zap,
  Bot
} from 'lucide-react';

const UserModal = ({
  isOpen,
  onClose,
  user,
  isEditMode,
  onEditModeChange,
  onUpdate,
  onDelete,
  onOpenGiftModal,
  onOpenSubscriptionModal,
  formatDate,
  getPremiumRemaining,
  getActivationTypeIcon,
  getActivationTypeText
}) => {
  const [editFormData, setEditFormData] = useState({
    fullName: user?.fullName || '',
    phone: user?.phone || '',
    email: user?.email || '',
    refBonus: user?.refBonus || 0,
    birthDate: user?.birthDate || '',
    savedAddress: user?.savedAddress?.street || '',
    deliveryInstructions: user?.savedAddress?.deliveryInstructions || ''
  });

  if (!isOpen || !user) return null;

  // Функции форматирования
  const formatDateDisplay = (date) => {
    if (!date) return 'Не указано';
    const d = new Date(date);
    if (isNaN(d.getTime())) return 'Не указано';
    return d.toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getPremiumRemainingDays = (expiresAt) => {
    if (!expiresAt) return 0;
    const now = new Date();
    const expiry = new Date(expiresAt);
    const diffTime = expiry - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  const getPremiumRemainingText = (expiresAt) => {
    const days = getPremiumRemainingDays(expiresAt);
    if (days === 0) return 'Истек';
    if (days === 1) return '1 день';
    if (days <= 4) return `${days} дня`;
    return `${days} дней`;
  };

  const getActivationTypeIconComponent = (type) => {
    if (type === 'manual') return <Zap className="w-3 h-3" />;
    if (type === 'auto') return <Bot className="w-3 h-3" />;
    return <Eye className="w-3 h-3" />;
  };

  const getActivationTypeTextValue = (type) => {
    if (type === 'manual') return 'Ручная выдача';
    if (type === 'auto') return 'Автоматическая';
    return 'Не указано';
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleUpdate = () => {
    onUpdate(user.id, editFormData);
  };

  const handleCancelEdit = () => {
    onEditModeChange(false);
    setEditFormData({
      fullName: user.fullName || '',
      phone: user.phone || '',
      email: user.email || '',
      refBonus: user.refBonus || 0,
      birthDate: user.birthDate || '',
      savedAddress: user.savedAddress?.street || '',
      deliveryInstructions: user.savedAddress?.deliveryInstructions || ''
    });
  };

  // Определяем цветовую схему в зависимости от типа пользователя
  const getUserTypeColor = () => {
    if (user.userType === 'telegram') {
      return {
        bg: 'from-blue-900/30 to-blue-800/30',
        border: 'border-blue-700',
        text: 'text-blue-400',
        badge: 'bg-blue-900/50 text-blue-300'
      };
    }
    return {
      bg: 'from-green-900/30 to-green-800/30',
      border: 'border-green-700',
      text: 'text-green-400',
      badge: 'bg-green-900/50 text-green-300'
    };
  };

  const userTypeColors = getUserTypeColor();

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-3 sm:p-4 z-50 backdrop-blur-sm">
      <div className="bg-gray-900 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-2xl flex flex-col border border-gray-700">
        {/* Заголовок */}
        <div className={`px-4 sm:px-6 py-4 border-b border-gray-700 flex items-center justify-between bg-gradient-to-r ${userTypeColors.bg} from-gray-800 to-gray-900`}>
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center flex-shrink-0 border ${
              user.premium 
                ? 'bg-gradient-to-br from-yellow-900/30 to-orange-900/30 border-yellow-700' 
                : userTypeColors.bg.includes('blue') 
                  ? 'bg-gradient-to-br from-gray-800 to-blue-900/30 border-blue-700'
                  : 'bg-gradient-to-br from-gray-800 to-green-900/30 border-green-700'
            }`}>
              {user.premium ? (
                <Crown className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-400" />
              ) : (
                <UserPlus className={`w-5 h-5 sm:w-6 sm:h-6 ${userTypeColors.text}`} />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-lg sm:text-xl font-bold text-white truncate">
                  {user.fullName || 'Пользователь без имени'}
                </h2>
                <span className={`px-2 py-0.5 text-xs rounded-full ${userTypeColors.badge} flex-shrink-0`}>
                  {user.userType === 'telegram' ? '📱 Telegram' : '🌐 Web'}
                </span>
              </div>
              <p className="text-sm text-gray-400 truncate">
                {user.userType === 'telegram' && user.username && `@${user.username} • `}
                ID: {user.id.substring(0, 8)}...
                {user.userType === 'web' && user.email && ` • ${user.email}`}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-300 hover:bg-gray-800 rounded-lg transition-colors ml-2 flex-shrink-0 border border-gray-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Контент */}
        <div className="p-4 sm:p-6 overflow-y-auto flex-1">
          {isEditMode ? (
            // Режим редактирования
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Полное имя
                  </label>
                  <input
                    type="text"
                    name="fullName"
                    value={editFormData.fullName}
                    onChange={handleInputChange}
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-800 text-white border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Телефон
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={editFormData.phone}
                    onChange={handleInputChange}
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-800 text-white border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={editFormData.email}
                    onChange={handleInputChange}
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-800 text-white border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Реферальный бонус
                  </label>
                  <input
                    type="number"
                    name="refBonus"
                    value={editFormData.refBonus}
                    onChange={handleInputChange}
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-800 text-white border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    День рождения
                  </label>
                  <input
                    type="date"
                    name="birthDate"
                    value={editFormData.birthDate}
                    onChange={handleInputChange}
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-800 text-white border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Адрес
                  </label>
                  <input
                    type="text"
                    name="savedAddress"
                    value={editFormData.savedAddress}
                    onChange={handleInputChange}
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-800 text-white border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                    placeholder="Улица, дом, квартира"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Инструкции по доставке
                </label>
                <textarea
                  name="deliveryInstructions"
                  value={editFormData.deliveryInstructions}
                  onChange={handleInputChange}
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-800 text-white border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                  rows="3"
                  placeholder="Код домофона, этаж, подъезд..."
                />
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3 pt-4 sm:pt-6 border-t border-gray-700">
                <button
                  onClick={handleUpdate}
                  className="flex-1 px-4 sm:px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors text-sm sm:text-base"
                >
                  Сохранить изменения
                </button>
                <button
                  onClick={handleCancelEdit}
                  className="px-4 sm:px-6 py-3 bg-gray-700 text-gray-300 font-medium rounded-lg hover:bg-gray-600 transition-colors text-sm sm:text-base"
                >
                  Отмена
                </button>
              </div>
            </div>
          ) : (
            // Режим просмотра
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                {/* Основная информация */}
                <div className="space-y-4">
                  <h3 className="text-base sm:text-lg font-semibold text-white flex items-center gap-2">
                    <UserPlus className="w-4 h-4 sm:w-5 sm:h-5" />
                    Основная информация
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-400">Тип пользователя:</span>
                      <span className={`text-sm font-medium ${userTypeColors.text} flex items-center gap-1`}>
                        {user.userType === 'telegram' ? '📱 Telegram' : '🌐 Web'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-400">Полное имя:</span>
                      <span className="text-sm font-medium text-gray-300 text-right truncate ml-2">
                        {user.fullName || 'Не указано'}
                      </span>
                    </div>
                    {user.userType === 'telegram' && (
                      <>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-400">Username:</span>
                          <span className="text-sm font-medium text-gray-300">
                            {user.username ? `@${user.username}` : 'Не указан'}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-400">Telegram ID:</span>
                          <code className="font-mono text-xs bg-gray-800 px-2 py-1 rounded truncate ml-2 text-gray-300 border border-gray-700">
                            {user.id}
                          </code>
                        </div>
                      </>
                    )}
                    {user.userType === 'web' && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-400">User ID:</span>
                        <code className="font-mono text-xs bg-gray-800 px-2 py-1 rounded truncate ml-2 text-gray-300 border border-gray-700">
                          {user.id}
                        </code>
                      </div>
                    )}
                  </div>
                </div>

                {/* Контактная информация */}
                <div className="space-y-4">
                  <h3 className="text-base sm:text-lg font-semibold text-white flex items-center gap-2">
                    <Phone className="w-4 h-4 sm:w-5 sm:h-5" />
                    Контактная информация
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-400">Телефон:</span>
                      <span className="text-sm font-medium text-gray-300 truncate ml-2">
                        {user.phone || 'Не указан'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-400">Email:</span>
                      <span className="text-sm font-medium text-gray-300 truncate ml-2">
                        {user.email || 'Не указан'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-400">День рождения:</span>
                      <span className="text-sm font-medium text-gray-300">
                        {formatDateDisplay(user.birthDate)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Premium доступ */}
                <div className="space-y-4">
                  <h3 className="text-base sm:text-lg font-semibold text-white flex items-center gap-2">
                    <Crown className="w-4 h-4 sm:w-5 sm:h-5" />
                    Premium доступ
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-400">Статус:</span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${
                        user.premium
                          ? 'bg-yellow-900/30 text-yellow-300 border-yellow-700'
                          : 'bg-gray-700 text-gray-300 border-gray-600'
                      }`}>
                        {user.premium ? 'Активен' : 'Не активен'}
                      </span>
                    </div>
                    
                    {user.premium && (
                      <>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-400">Тип активации:</span>
                          <span className="text-sm font-medium text-gray-300 flex items-center gap-1">
                            {getActivationTypeIconComponent(user.premiumActivationType)}
                            {getActivationTypeTextValue(user.premiumActivationType)}
                          </span>
                        </div>
                        
                        {user.premiumActivatedAt && (
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-400">Активирован:</span>
                            <span className="text-sm font-medium text-gray-300">
                              {formatDateDisplay(user.premiumActivatedAt)}
                            </span>
                          </div>
                        )}
                        
                        {user.premiumExpiresAt && (
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-400">Истекает:</span>
                            <span className="text-sm font-medium text-blue-400">
                              {formatDateDisplay(user.premiumExpiresAt)} 
                              <span className="text-gray-400 ml-1">
                                ({getPremiumRemainingText(user.premiumExpiresAt)})
                              </span>
                            </span>
                          </div>
                        )}

                        {user.subscription?.planName && (
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-400">Тариф:</span>
                            <span className="text-sm font-medium text-purple-400">
                              {user.subscription.planName}
                            </span>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Финансы */}
                <div className="space-y-4">
                  <h3 className="text-base sm:text-lg font-semibold text-white flex items-center gap-2">
                    <CreditCard className="w-4 h-4 sm:w-5 sm:h-5" />
                    Финансы
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-400">Реферальный бонус:</span>
                      <span className="text-sm font-medium text-green-400">
                        {user.refBonus?.toLocaleString() || 0} €
                      </span>
                    </div>
                    {user.referrerId && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-400">Пригласил:</span>
                        <code className="font-mono text-xs bg-gray-800 px-2 py-1 rounded truncate ml-2 text-gray-300 border border-gray-700">
                          {user.referrerId.substring(0, 8)}...
                        </code>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Адрес */}
              {user.savedAddress?.street && (
                <div className="space-y-4">
                  <h3 className="text-base sm:text-lg font-semibold text-white flex items-center gap-2">
                    <MapPin className="w-4 h-4 sm:w-5 sm:h-5" />
                    Адрес доставки
                  </h3>
                  <div className="bg-gray-800 p-4 rounded-lg space-y-2 border border-gray-700">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-400">Адрес:</span>
                      <span className="text-sm font-medium text-gray-300 text-right truncate ml-2 max-w-[60%]">
                        {user.savedAddress.street}
                      </span>
                    </div>
                    {user.savedAddress.deliveryInstructions && (
                      <div className="flex justify-between items-start">
                        <span className="text-sm text-gray-400">Инструкции:</span>
                        <span className="text-sm font-medium text-gray-300 text-right truncate ml-2 max-w-[60%]">
                          {user.savedAddress.deliveryInstructions}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Кнопки действий */}
              <div className="flex flex-col sm:flex-row gap-3 pt-4 sm:pt-6 border-t border-gray-700">
                <button
                  onClick={() => onEditModeChange(true)}
                  className="flex-1 px-4 sm:px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 text-sm sm:text-base"
                >
                  <Edit className="w-4 h-4" />
                  Редактировать
                </button>
                
                <button
                  onClick={() => {
                    onClose();
                    onOpenGiftModal(user);
                  }}
                  className="flex-1 px-4 sm:px-6 py-3 bg-pink-600 text-white font-medium rounded-lg hover:bg-pink-700 transition-colors flex items-center justify-center gap-2 text-sm sm:text-base"
                >
                  <Gift className="w-4 h-4" />
                  Управление подарками
                </button>
                
                {user.premium ? (
                  <>
                    <button
                      onClick={() => {
                        onClose();
                        onOpenSubscriptionModal(user, 'extend');
                      }}
                      className="flex-1 px-4 sm:px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 text-sm sm:text-base"
                    >
                      <Calendar className="w-4 h-4" />
                      Продлить
                    </button>
                    <button
                      onClick={() => {
                        onClose();
                        onOpenSubscriptionModal(user, 'revoke');
                      }}
                      className="flex-1 px-4 sm:px-6 py-3 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-2 text-sm sm:text-base"
                    >
                      <ShieldOff className="w-4 h-4" />
                      Отозвать
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => {
                      onClose();
                      onOpenSubscriptionModal(user, 'grant');
                    }}
                    className="flex-1 px-4 sm:px-6 py-3 bg-yellow-600 text-white font-medium rounded-lg hover:bg-yellow-700 transition-colors flex items-center justify-center gap-2 text-sm sm:text-base"
                  >
                    <Crown className="w-4 h-4" />
                    Выдать Premium
                  </button>
                )}
                
                <button
                  onClick={() => {
                    if (window.confirm('Удалить пользователя?')) {
                      onDelete(user.id);
                    }
                  }}
                  className="flex-1 px-4 sm:px-6 py-3 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-2 text-sm sm:text-base"
                >
                  <Trash2 className="w-4 h-4" />
                  Удалить
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserModal;